import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useCertificateDownload, useCertificateStatus } from '@hooks'
import { CertificateDownloadInput, LoadingStatus, PageBreadcrumbs } from '@components/Shared'
import { vesselService, vesselStoreService } from '@services'
import { getValidFrom, getValidToDisplay } from '@utils'
import { useLanguage } from '@i18n'
import type { Vessel } from '@app-types/index'
import './Certificate.css'

const getCertificateTypeDisplayName = (
  type: string | undefined,
  fallback: string | undefined,
  t: (key: string) => string
) => {
  if (!type) {
    return fallback || '-'
  }

  const typeName = t(`certificateTypes.${type}`)
  return typeName === `certificateTypes.${type}` ? fallback || type : typeName
}

const utnPattern = /^([A-Z0-9]{1,7}-)([A-Z0-9]{2,7}-)([A-Z0-9]{2}-)([A-Z0-9]{7})$/

export const Certificate = () => {
  const { vesselID, certificateID } = useParams<{ vesselID?: string; certificateID?: string }>()
  const [searchParams] = useSearchParams()
  const { t } = useLanguage()
  const [storedVessel, setStoredVessel] = useState<Vessel | null>(null)
  const [downloadedVessel, setDownloadedVessel] = useState<Vessel | null>(null)
  const [storageChecked, setStorageChecked] = useState(false)
  const [utnNumber, setUtnNumber] = useState('')
  const { clearDownloadError, downloadCertificate, downloadError, isDownloading } = useCertificateDownload()
  const query = certificateID || searchParams.get('id') || ''
  const apiQuery = storageChecked ? query : ''
  const { data, loading, error } = useCertificateStatus(apiQuery, true)
  const vessel = downloadedVessel || data || storedVessel
  const certificate = useMemo(
    () => data?.certificates?.[0]
      || vessel?.certificates?.find((item) => item.uuid === query || item.seqNo === query)
      || null,
    [data, query, vessel]
  )

  useEffect(() => {
    setStoredVessel(null)
    setDownloadedVessel(null)
    setStorageChecked(false)
    clearDownloadError()

    if (!vesselID) {
      setStorageChecked(true)
      return
    }

    let cancelled = false

    const loadStoredVessel = async () => {
      const localVessel = await vesselService.get(vesselID)
      const memoryVessel = vesselStoreService.getVessel()
      const matchingMemoryVessel =
        memoryVessel && String(memoryVessel.vesselID) === String(vesselID) ? memoryVessel : null

      if (!cancelled) {
        setStoredVessel(localVessel || matchingMemoryVessel)
        setStorageChecked(true)
      }
    }

    void loadStoredVessel()

    return () => {
      cancelled = true
    }
  }, [clearDownloadError, vesselID])

  useEffect(() => {
    setUtnNumber(certificate?.seqNo || (utnPattern.test(query) ? query : ''))
  }, [certificate, query])

  const handleDownload = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    void downloadCertificate(certificate, utnNumber, {
      onDownloadedVessel: setDownloadedVessel,
      vesselName: vessel?.vesselName,
    })
  }

  if (!storageChecked || (apiQuery && loading)) {
    return <LoadingStatus className="certificate loading" label={t('certificate.loading')} />
  }

  if (error) {
    return <div className="certificate error">{t('certificate.errorLoading', { message: error.message })}</div>
  }

  if (!vessel || !certificate) {
    return <div className="certificate not-found">{t('certificate.notFound')}</div>
  }

  const vesselPath = `/vessels/vessel/${vessel.vesselID}`
  const certificatePath = vesselID
    ? `/vessels/vessel/${vessel.vesselID}/${encodeURIComponent(query)}`
    : `/certificate?id=${encodeURIComponent(query)}`
  const certificateLabel = certificate.type || certificate.name || t('certificate.titleFallback')
  const certificateTypeDisplayName = getCertificateTypeDisplayName(certificate.type, certificate.name, t)
  const certificateDetails = [
    {
      id: 'certificate-type',
      label: t('certificate.type'),
      content: certificateTypeDisplayName,
    },
    {
      id: 'status',
      label: t('common.status'),
      content: <span className="certificate__status-tag">{certificate.status || '-'}</span>,
    },
    {
      id: 'imo',
      label: 'IMO',
      content: vessel.imoNumber || '-',
    },
    {
      id: 'valid-from',
      label: t('certificate.validFrom'),
      content: getValidFrom(certificate),
    },
    {
      id: 'valid-to',
      label: t('certificate.validTo'),
      content: getValidToDisplay(certificate, t('certificate.noExpiry')),
    },
  ]

  return (
    <section className="certificate">
      <PageBreadcrumbs
        backLabel={
          vesselID ? t('breadcrumbs.backToVessel', { vesselName: vessel.vesselName }) : t('breadcrumbs.backToVessels')
        }
        backText={vesselID ? t('breadcrumbs.vessel') : undefined}
        backTo={vesselID ? vesselPath : '/vessels'}
        items={
          vesselID
            ? [
                { label: t('breadcrumbs.vessels'), to: '/vessels' },
                { label: vessel.vesselName, to: vesselPath },
                { label: certificateLabel, to: certificatePath },
              ]
            : [
                { label: t('breadcrumbs.vessels'), to: '/vessels' },
                { label: certificateLabel, to: certificatePath },
              ]
        }
      />

      <div className="certificate__hero">
        <h1>{vessel.vesselName}</h1>
      </div>

      <div className="certificate__content">
        <div className="certificate__card">
          <dl
            className="certificate__details"
            aria-label={t('certificate.detailsAriaLabel', {
              certificateType: certificate.type || t('certificate.titleFallback'),
              vesselName: vessel.vesselName,
            })}
          >
            {certificateDetails.map((detail) => (
              <div className="certificate__details-row" key={detail.id}>
                <dt>{detail.label}</dt>
                <dd>{detail.content}</dd>
              </div>
            ))}
          </dl>
        </div>

        <form className="certificate__download-form" onSubmit={handleDownload}>
          <div className="certificate__download-heading">
            <div>
              <h3>{t('certificate.downloadHeading')}</h3>
            </div>
          </div>

          <CertificateDownloadInput
            id="certificate-utn"
            label={t('certificateDownload.inputLabel')}
            hideLabel
            value={utnNumber}
            error={downloadError}
            loading={isDownloading}
            disabled={!certificate.pdf && !utnNumber.trim()}
            onChange={(value) => {
              setUtnNumber(value)
              clearDownloadError()
            }}
          />
        </form>
      </div>
    </section>
  )
}

export default Certificate
